import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckSquare } from 'lucide-react';

import {
  getCommitment,
  listCommitments,
} from '../../services/projectRegisterApi';
import CommitmentDetailPanel from './CommitmentDetailPanel';
import {
  Chip,
  DrawerDetail,
  EmptyState,
  ErrorBanner,
  FilterBar,
  FilterSelect,
  Spinner,
  T,
  fmtDate,
  fmtDateTime,
  humanise,
  personLabel,
  shortRef,
  useDetailNav,
} from './shared';

const STATUS_TONE = {
  OPEN: { fg: T.info, bg: T.infoBg },
  FULFILLED: { fg: T.ok, bg: T.okBg },
  MET: { fg: T.ok, bg: T.okBg },
  STALLED: { fg: T.warn, bg: T.warnBg },
  MISSED: { fg: T.danger, bg: T.dangerBg },
  NEEDS_CONFIRMATION: { fg: T.textSec, bg: 'rgba(209,209,199,0.08)' },
};

const CommitmentRow = ({ commitment, selected, onSelect }) => {
  const tone = STATUS_TONE[commitment.status] || {
    fg: T.textSec,
    bg: 'rgba(212,212,216,0.08)',
  };
  // tracker.followup_log is chronological (oldest first, per the tracker
  // service that appends to it) — last entry is the most recent followup.
  const followupLog = commitment.tracker?.followup_log;
  const lastFollowup =
    followupLog?.length > 0 ? followupLog[followupLog.length - 1] : null;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full rounded-2xl p-3 text-left transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5652CF]/40 ${
        selected
          ? 'border border-indigo-500/60 bg-indigo-950/20 shadow-md shadow-indigo-950/30 ring-1 ring-indigo-500/50'
          : 'border border-zinc-800/80 bg-zinc-900/80 shadow-sm hover:border-zinc-700 hover:bg-zinc-800/60'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-medium uppercase tracking-wide text-zinc-400">
              {shortRef(commitment.entity_id)}
            </span>
            <Chip bordered uppercase>
              {humanise(commitment.firmness)}
            </Chip>
            {commitment.stall_flag && (
              <Chip fg={T.warn} bg={T.warnBg} uppercase>
                Stalled
              </Chip>
            )}
          </div>
          <div className="mt-2 truncate text-[13px] font-medium text-zinc-100">
            {commitment.normalised_text || shortRef(commitment.entity_id)}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-zinc-400">
            <span className="font-medium text-zinc-300">
              {personLabel(commitment.committer_person_id) || 'Unassigned'}
            </span>
            {commitment.deadline_date && (
              <>
                <span>·</span>
                <span>due {fmtDate(commitment.deadline_date)}</span>
              </>
            )}
            {lastFollowup && (
              <>
                <span>·</span>
                <span>followed up {fmtDateTime(lastFollowup.at)}</span>
              </>
            )}
            {commitment.related_concern_ref && (
              <>
                <span>·</span>
                <span className="text-amber-400">
                  {commitment.related_concern_ref}
                </span>
              </>
            )}
          </div>
        </div>
        <Chip fg={tone.fg} bg={tone.bg}>
          {humanise(commitment.status)}
        </Chip>
      </div>
    </button>
  );
};

const CommitmentsList = ({ accessToken }) => {
  const [commitments, setCommitments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('ALL');
  const {
    selectedId,
    detail,
    loading: detailLoading,
    error: detailError,
    open: openDetail,
    close: closeDetail,
  } = useDetailNav((entityId) => getCommitment(accessToken, entityId));

  const reload = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const response = await listCommitments(accessToken);
      setCommitments(response?.commitments || []);
    } catch (loadError) {
      setError(String(loadError?.message || loadError));
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    reload();
  }, [reload]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return commitments.filter((commitment) => {
      if (status !== 'ALL' && commitment.status !== status) return false;
      if (!q) return true;
      const haystack = [
        commitment.normalised_text,
        commitment.entity_id,
        commitment.committer_person_id,
        commitment.related_concern_ref,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [commitments, search, status]);

  if (loading) return <Spinner />;

  return (
    // h-full min-h-0: fills whatever the page frame gives this tab instead
    // of growing past it. The banner/filter row stays put (shrink-0); only
    // the list below scrolls, in its own overflow-y-auto -- the filter no
    // longer scrolls away with it.
    <div className="flex h-full min-h-0 flex-col gap-2">
      <div className="flex shrink-0 flex-col gap-2">
        <ErrorBanner>{error}</ErrorBanner>
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          placeholder="Search commitments"
        >
          <FilterSelect
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="ALL">All statuses</option>
            <option value="OPEN">Open</option>
            <option value="FULFILLED">Fulfilled</option>
            <option value="STALLED">Stalled</option>
            <option value="NEEDS_CONFIRMATION">Needs confirmation</option>
          </FilterSelect>
        </FilterBar>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-1">
        {filtered.length === 0 ? (
          <EmptyState icon={CheckSquare}>No commitments match.</EmptyState>
        ) : (
          <div className="flex flex-col gap-2">
            {filtered.map((commitment) => (
              <CommitmentRow
                key={commitment.entity_id}
                commitment={commitment}
                selected={commitment.entity_id === selectedId}
                onSelect={() => openDetail(commitment.entity_id)}
              />
            ))}
          </div>
        )}
      </div>

      <DrawerDetail open={!!selectedId} onClose={closeDetail}>
        {detailLoading ? (
          <Spinner />
        ) : detailError ? (
          <div className="p-6">
            <ErrorBanner>{detailError}</ErrorBanner>
          </div>
        ) : (
          <CommitmentDetailPanel detail={detail} onClose={closeDetail} />
        )}
      </DrawerDetail>
    </div>
  );
};

export default CommitmentsList;
